<template>
  <div class="wrapper">
    <div class="url__header">
      <div class="url__header-title"
           :title="title">{{title}}</div>
      <div class="url__export-button url__header-icon"
           @click="exportCsv()">
        <base-material-design-icon icon="download" />
      </div>
    </div>
    <div class="url__content">
      <div class="common-table__row common-table__header">
        <div class="common-table__header-subtitle">Rank Zone</div>
        <div class="common-table__header-cell url__first">Snapshot Date</div>
        <div v-for="cell in commonCells"
             :key="cell"
             class="common-table__header-cell common-table__header-cell_shifted">{{cell}}</div>
        <div class="common-table__header-cell url__last">Total</div>
      </div>
      <template v-for="rowIndex in rows">
        <div class="common-table__row" :key="rowIndex">
          <div class="common-table__cell url__first">
            <base-select class="url__select"
                         v-model="selectedSnapshots[rowIndex]"
                         :showTopBorder="true"
                         :options="preparedSnapshots"
                         :selectStyle="{height: '60px!important'}"
                         borderLess
                         @change="changeDate(rowIndex)"/>
          </div>
          <div v-if="!isUrlSnapshotsDataLoaded[rowIndex]"
               class="common-table__cell url__loader-wrap">
            <base-horizontal-loader class="url__loader" />
          </div>
          <div v-else-if="isUrlSnapshotDataEmpty[rowIndex]"
               class="common-table__cell url__no-data">Awaiting Data</div>
          <template v-else>
            <div v-for="cell in commonCells"
                 :key="cell"
                 class="common-table__cell">
              {{urlSnapshotsData[rowIndex][cell] | roundNumber | commaDividedNumber}}
            </div>
            <div class="common-table__cell url__last">
              {{urlSnapshotsData[rowIndex]['total_msv'] | roundNumber | commaDividedNumber}}
            </div>
          </template>
        </div>
      </template>
    </div>
    <div class="url__controls">
      <button class="url__button url__button_submit" @click.prevent="cancel">Close</button>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import { mapState, mapActions } from 'vuex'
import filters from '@/common/Filters'
import csvExport from '@/services/CSVExport'
import helper from '@/services/Helper'
import { convertAdvancedOptionsToFilters } from '@/features/reports/common/advanced-options/config'
import moment from 'moment'
import Url from '../rank-zone/url/Url'

library.add(
  faDownload
)
/**
 * Url modal with comparison table
 * Used on {@link Url} tab
 */
export default {
  name: 'UrlPopup',
  data() {
    return {
      commonCells: [
        '01 - 03',
        '04 - 07',
        '08 - 10',
        '11 - 25',
        '26 - 50',
        '51 - 100',
        'NR',
        'ND'
      ],
      isDataFetched: false,
      rows: [0, 1],
      selectedSnapshots: []
    }
  },
  created () {
    // prepare default snapshot data
    const firstSnapshotIndex = this.snapshots.length - 1
    const secondSnapshotIndex = 0
    this.selectedSnapshots = [
      this.snapshots[ firstSnapshotIndex ],
      this.snapshots[ secondSnapshotIndex ]
    ]
    this.getData()
  },
  props: {
    title: {
      type: String,
      required: true
    },
    client: {
      type: String,
      required: true
    },
    url: {
      type: String,
      required: true
    },
    date: {
      type: String,
      required: true
    },
    selectedAdvancedOptions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapState('rankZoneCommon', [
      'snapshots',
      'urlSnapshotsData'
    ]),
    preparedSnapshots () {
      const snapshots = helper.sortSnapshots(this.snapshots)
      return snapshots.map(snapshot => ({
        label: this.$options.filters.formattedDate(snapshot),
        value: snapshot
      }))
    },
    isUrlSnapshotsDataLoaded () {
      return [
        Boolean(this.urlSnapshotsData[0]),
        Boolean(this.urlSnapshotsData[1])
      ]
    },
    isUrlSnapshotDataEmpty () {
      return [
        !this.checkUrlSnapshotData(this.urlSnapshotsData[0]),
        !this.checkUrlSnapshotData(this.urlSnapshotsData[1])
      ]
    }
  },
  methods: {
    ...mapActions('rankZoneCommon', [
      'getUrlSnapshotData'
    ]),
    cancel () {
      this.$emit('close')
    },
    getData () {
      this.getSnapshot()
    },
    getSnapshot: function (indexes = [0, 1]) {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const payload = {
        clientId: this.client,
        filters: {
          snapshot_date_1: this.selectedSnapshots[0],
          snapshot_date_2: this.selectedSnapshots[1],
          url_id: this.url,
          advancedFilters
        },
        indexes
      }
      this.getUrlSnapshotData(payload)
    },
    changeDate (index) {
      this.getSnapshot([0, 1])
    },
    checkUrlSnapshotData(data) {
      if (!data) {
        return false
      }
      return Object.keys(data).length > 0
    },
    exportCsv () {
      if (!this.isUrlSnapshotsDataLoaded[0] || !this.isUrlSnapshotsDataLoaded[1]) {
        return
      }
      const header = ['Snapshot Date', 'URL', ...this.commonCells, 'Total']
      const cells = [...this.commonCells, 'total_msv']
      const body = Object.values(this.urlSnapshotsData).map((snapshotData, index) => {
        const result = [this.selectedSnapshots[index]]
        result.push(this.title)
        cells.forEach(cell => {
          const value = snapshotData[cell]
          result.push(value === undefined ? '' : value)
        })
        result.push()
        return result
      })
      csvExport(`${this.selectedClient.name.replace(/ /g, '_')}_RankZone_URL_${moment().format('YYYY-MM-DD')}`, [header, ...body])
    }
  },
  filters: {
    ...filters
  }
}
</script>

<style scoped lang="scss">

  .wrapper {
    padding: 40px;
    background-color: #fff;
    border-radius: 13px;
  }

  .url {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 30px;

    &__header {
      display: flex;
      justify-content: center;
      width: 100%;
      height: 40px;
      position: relative;
      margin-bottom: 30px;

      &-title {
        text-align: center;
        font-size: 34px;
        font-weight: 500;
        max-width: 840px;
        @include ellipsis;
      }

      &-icon {
        position: absolute;
        top: 14px;
        right: 0;
        color: $rfx-bright-blue;
      }
    }

    &__first {
      flex: 0 0 200px;
      box-sizing: border-box;
      border-top-left-radius: $rfx-default-border-radius;
    }

    &__last {
      flex: 0 0 100px;
      box-sizing: border-box;
      border-top-right-radius: $rfx-default-border-radius;
    }

    &__no-data {
      font-size: 27px;
      padding: 0;
      text-align: center;
      height: 60px;
      line-height: 60px;
      font-weight: 600;
      box-sizing: border-box;
    }

    &__loader-wrap {
      height: 60px;
      box-sizing: border-box;
      overflow: hidden;
      padding: 0;
      position: relative;
    }

    &__loader {
      position: absolute;
      top: -2px;
      left: 50%;
      transform: translateX(-50%);
    }

    &__table {
      margin-top: 40px;
      max-height: calc(85vh - 245px);
      display: flex;
      flex-direction: column;
    }

    &__select {
      flex: 1 1;
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
      margin-top: 30px;
    }

    &__button {
      width: 70px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;
      }
    }
  }

  .common-table {
    &__header-subtitle {
      position: absolute;
      height: 28px;
      top: 0;
      left: 200px;
      z-index: 1;
      color: $rfx-dark-grey;
      font-weight: 500;
      background: $rfx-pale-grey;
      width: calc(100% - 300px);
      text-align: center;
      line-height: 28px;
    }

    &__header {
      position: relative;
    }

    &__header-cell {

      @include ellipsis;
      color: $rfx-dark-grey;
      box-sizing: border-box;

      &_shifted {
        border-top: 1px solid rgba(0, 0, 0, .05);
        padding-top: 6px;
        padding-bottom: 0;
        padding-left: 0;
        margin-top: 28px;
      }
    }

    &__cell {
      &:first-child {
        padding: 0 20px 0 0;
        display: flex;
        align-items: center;
        height: 60px;
      }

      &:not(:last-child) {
        padding-left: 0;
      }
    }
  }
</style>
